import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {Project} from './project'
import type {GraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {buildItemPickerSearchUrl} from '@github-ui/item-picker-base/api/urls'

/**
 * The types here align with the query made on the server defined here:
 * https://github.com/github/github/blob/master/app/controllers/ghui/item_pickers/projects_controller.rb
 * If the shape of that response changes, this type should be
 * updated to reflect any modified fields
 */

export type GetSearchProjectsRequest = {
  owner: string
  repo: string
  query?: string
  count?: number
}

type GetSearchProjectsResponseData = {
  repository: {
    id: string
    projectsV2: {
      nodes: Array<Project | null> | null
    }
    recentProjects: {
      edges: Array<{node: Project | null} | null> | null
    }
    owner: {
      projectsV2: {
        edges: Array<{node: Project | null} | null> | null
      }
      recentProjects: {
        edges: Array<{node: Project | null} | null> | null
      }
    }
  }
}

export type GetSearchProjectsResponse = GraphQLResponse<GetSearchProjectsResponseData>

export async function apiGetSearchProjects({
  owner,
  repo,
  query,
  count,
}: GetSearchProjectsRequest): Promise<GetSearchProjectsResponse> {
  const params: Record<string, unknown> = {owner, repo}

  if (query) {
    params['query'] = query
  }

  if (count) {
    params['count'] = count
  }

  const requestUrl = buildItemPickerSearchUrl('projects', params)
  const request = await verifiedFetchJSON(requestUrl)
  return (await request.json()) as GetSearchProjectsResponse
}

const itemPickerQueryKeyBase = 'item_picker'

export function buildProjectPickerSearchQueryKey(searchParams: GetSearchProjectsRequest) {
  return [itemPickerQueryKeyBase, 'projects', searchParams]
}

export type ProjectPickerQueryData = {
  repositoryId: string
  repositoryProjects: Project[]
  recentRepositoryProjects: Project[]
  ownerProjects: Project[]
  recentOwnerProjects: Project[]
}

export function transformSearchProjectResponseToQueryData(
  response: GetSearchProjectsResponseData,
): ProjectPickerQueryData {
  return {
    repositoryId: response.repository.id,
    repositoryProjects: (response.repository.projectsV2.nodes ?? []).flatMap(node => (node ? [node] : [])),
    recentRepositoryProjects: (response.repository.recentProjects.edges ?? []).flatMap(edge =>
      edge?.node ? [edge.node] : [],
    ),
    ownerProjects: (response.repository.owner.projectsV2.edges ?? []).flatMap(edge => (edge?.node ? [edge.node] : [])),
    recentOwnerProjects: (response.repository.owner.recentProjects.edges ?? []).flatMap(edge =>
      edge?.node ? [edge.node] : [],
    ),
  }
}
